import * as THREE from 'three';
import { FikoARAnchor } from './FikoARAnchor';
import { FikoARKontrol } from './FikoARKontrol';

export type ARModuDurum = 'kapali' | 'baslatiliyor' | 'aktif' | 'hata';
export type KameraYonu = 'arka' | 'on';

export class FikoARModu {
  durum: ARModuDurum = 'kapali';
  anchor: FikoARAnchor;
  kontrol: FikoARKontrol | null = null;

  private videoElement: HTMLVideoElement | null = null;
  private mediaStream: MediaStream | null = null;
  private videoContainer: HTMLDivElement | null = null;
  private arKamera: THREE.PerspectiveCamera | null = null;
  private oncekiKamera: THREE.Camera | null = null;
  private oncekiKontrol: any = null;
  private canvasParent: HTMLElement | null = null;
  private kameraYonu: KameraYonu = 'arka';
  private _modelOpakligi = 0.85;
  private _oncekiClearColor = new THREE.Color();
  private _oncekiClearAlpha = 1;

  private sunucu: THREE.WebGLRenderer | null = null;
  private markNeedsRender: (() => void) | null = null;

  constructor() {
    this.anchor = new FikoARAnchor();
  }

  get aktif(): boolean {
    return this.durum === 'aktif';
  }

  get modelOpakligi(): number {
    return this._modelOpakligi;
  }

  set modelOpakligi(deger: number) {
    this._modelOpakligi = Math.max(0.1, Math.min(1, deger));
  }

  async basla(
    canvasParent: HTMLElement,
    sunucu: THREE.WebGLRenderer,
    mevcutKamera: THREE.Camera,
    mevcutKontrol: any,
    markNeedsRenderFn: () => void
  ): Promise<boolean> {
    if (this.durum === 'aktif') return true;
    this.durum = 'baslatiliyor';

    this.canvasParent = canvasParent;
    this.sunucu = sunucu;
    this.markNeedsRender = markNeedsRenderFn;
    this.oncekiKamera = mevcutKamera;
    this.oncekiKontrol = mevcutKontrol;

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Kamera erişimi desteklenmiyor');
      }

      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: this.kameraYonu === 'arka' ? 'environment' : 'user',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });

      this.videoOlustur();
      this.arKameraOlustur(canvasParent);

      const hostEl = canvasParent.closest('fikoyt-3d') as HTMLElement;
      if (hostEl) hostEl.classList.add('ar-modu-aktif');

      this._oncekiClearColor.copy(sunucu.getClearColor(new THREE.Color()));
      this._oncekiClearAlpha = sunucu.getClearAlpha();
      sunucu.setClearColor(0x000000, 0);

      if (mevcutKontrol?.enabled !== undefined) {
        mevcutKontrol.enabled = false;
      }

      this.kontrol = new FikoARKontrol(
        this.arKamera!,
        sunucu.domElement,
        this.anchor,
        markNeedsRenderFn
      );

      this.durum = 'aktif';
      markNeedsRenderFn();
      return true;
    } catch (e: any) {
      console.error('[FikoAR] Kamera başlatılamadı:', e);
      this.durum = 'hata';
      this.temizle();
      return false;
    }
  }

  durdur(): void {
    if (this.durum === 'kapali') return;

    const hostEl = this.canvasParent?.closest('fikoyt-3d') as HTMLElement;
    if (hostEl) hostEl.classList.remove('ar-modu-aktif');

    if (this.oncekiKontrol?.enabled !== undefined) {
      this.oncekiKontrol.enabled = true;
    }

    if (this.sunucu) {
      this.sunucu.setClearColor(this._oncekiClearColor, this._oncekiClearAlpha);
    }

    this.kontrol?.dispose();
    this.kontrol = null;
    this.anchor.serbest();
    this.temizle();
    this.durum = 'kapali';
    this.markNeedsRender?.();
  }

  sabitle(): void {
    this.anchor.sabitle();
    if (this.kontrol) this.kontrol.anchorGuncellendi();
    this.markNeedsRender?.();
  }

  serbest(): void {
    this.anchor.serbest();
    if (this.kontrol) this.kontrol.anchorGuncellendi();
    this.markNeedsRender?.();
  }

  sifirla(): void {
    this.anchor.sifirla();
    if (this.arKamera) {
      this.arKamera.position.set(0, 0, 100);
      this.arKamera.lookAt(0, 0, 0);
    }
    this.markNeedsRender?.();
  }

  async kameraDegistir(): Promise<void> {
    this.kameraYonu = this.kameraYonu === 'arka' ? 'on' : 'arka';
    if (this.durum !== 'aktif') return;

    this.kameraKapat();
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: this.kameraYonu === 'arka' ? 'environment' : 'user',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });
      if (this.videoElement) {
        this.videoElement.srcObject = this.mediaStream;
        this.videoElement.style.transform = this.kameraYonu === 'on' ? 'scaleX(-1)' : 'none';
      }
    } catch (e) {
      console.error('[FikoAR] Kamera değiştirme hatası:', e);
    }
  }

  getARKamera(): THREE.PerspectiveCamera | null {
    return this.arKamera;
  }

  getOncekiKamera(): THREE.Camera | null {
    return this.oncekiKamera;
  }

  private videoOlustur(): void {
    if (!this.canvasParent) return;

    this.canvasParent.style.position = 'relative';

    this.videoContainer = document.createElement('div');
    this.videoContainer.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      z-index: 0; overflow: hidden; pointer-events: none;
    `;

    this.videoElement = document.createElement('video');
    this.videoElement.autoplay = true;
    this.videoElement.playsInline = true;
    this.videoElement.muted = true;
    this.videoElement.setAttribute('playsinline', '');
    this.videoElement.setAttribute('webkit-playsinline', '');
    this.videoElement.style.cssText = `
      width: 100%; height: 100%; object-fit: cover;
      transform: ${this.kameraYonu === 'on' ? 'scaleX(-1)' : 'none'};
    `;

    this.videoContainer.appendChild(this.videoElement);
    this.canvasParent.insertBefore(this.videoContainer, this.canvasParent.firstChild);

    this.videoElement.srcObject = this.mediaStream;
    this.videoElement.play().catch(e => console.error('[FikoAR] Video oynatma hatası:', e));

    const canvas = this.sunucu?.domElement;
    if (canvas) {
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.zIndex = '1';
    }
    const css3d = this.canvasParent.querySelector('#canvasCSS3d') as HTMLElement;
    if (css3d) {
      css3d.style.position = 'absolute';
      css3d.style.top = '0';
      css3d.style.left = '0';
      css3d.style.zIndex = '2';
    }
  }

  private arKameraOlustur(container: HTMLElement): void {
    const w = container.clientWidth;
    const h = container.clientHeight;
    this.arKamera = new THREE.PerspectiveCamera(60, w / h, 0.1, 200000);

    if (this.oncekiKamera) {
      this.arKamera.position.copy(this.oncekiKamera.position);
      this.arKamera.quaternion.copy(this.oncekiKamera.quaternion);
    } else {
      this.arKamera.position.set(0, 0, 100);
      this.arKamera.lookAt(0, 0, 0);
    }
  }

  boyutGuncelle(w: number, h: number): void {
    if (this.arKamera) {
      this.arKamera.aspect = w / h;
      this.arKamera.updateProjectionMatrix();
    }
  }

  private kameraKapat(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(t => t.stop());
      this.mediaStream = null;
    }
  }

  private temizle(): void {
    this.kameraKapat();

    const canvas = this.sunucu?.domElement;
    if (canvas) {
      canvas.style.position = '';
      canvas.style.top = '';
      canvas.style.left = '';
      canvas.style.zIndex = '';
    }
    const css3d = this.canvasParent?.querySelector('#canvasCSS3d') as HTMLElement;
    if (css3d) {
      css3d.style.position = '';
      css3d.style.top = '';
      css3d.style.left = '';
      css3d.style.zIndex = '';
    }
    if (this.canvasParent) {
      this.canvasParent.style.position = '';
    }

    if (this.videoContainer?.parentNode) {
      this.videoContainer.parentNode.removeChild(this.videoContainer);
    }
    this.videoContainer = null;
    this.videoElement = null;
    this.arKamera = null;
  }

  dispose(): void {
    this.durdur();
  }
}
